import gdpr from 'common/report/util/gdpr-globals';
import {synchronizeOptState} from './opt';

import './banner';

// Make gpdr data accessible globally (useful for debugging)
window.gdprData = gdpr;

/* Allow delaying execution of snippets until GDPR has been initialized
 * so that opt out/in state can be checked before executing them
 */
const snippets = [];
window.gdprRestrictSnippet = (snippetFunc) => {
  if (gdpr.isReady && gdpr.allowSnippets) {
    snippetFunc();
  } else {
    snippets.push(snippetFunc);
  }
};

function loadIsGdpr() {
  if (gdpr.isReady) {
    return Promise.resolve(gdpr.isGdpr);
  }

  // gdpr.isGdpr state must be requested from server
  const token = window.mixpanel.get_config(`token`);
  return fetch(`https://mixpanel.com/api/app/gdpr`, {
    method: `GET`,
    headers: {Authorization: `Bearer ${token}`},
  }).then((response) => response.json())
    .then((data) => {
      gdpr.update({'is_gdpr': !!(data.results && data.results[`is_gdpr`])});
      return gdpr.isGdpr;
    });
}

/* Initialize GDPR functionality
 * - synchronize opt in/out state across Mixpanel libs and AppDB
 *   and update tracking and storage behavior appropriately
 * - either show or remove the GDPR banner depending on whether the user
 *   is from GDPR country and has opted in or out yet
 * - if we're on the privacy settings page, update toggles to match current opt state,
 *   pulled from Mixpanel lib and AppDB
 */
function initializeGdpr() {
  synchronizeOptState();

  const banner = document.getElementsByTagName(`mp-gdpr-banner`)[0];
  const settings = document.getElementsByTagName(`mp-gdpr-settings-privacy`)[0];

  // show or remove banner appropriately
  if (banner) {
    if (gdpr.showBanner) {
      banner.show();
    } else {
      banner.dismiss();
    }
  }

  // settings opt in/out state defaults to false and is updated to match
  // current state across Mixpanel libs and AppDB once the libs are loaded
  if (settings) {
    settings.updateToggle(`tracking`, gdpr.allowTracking);
    settings.updateToggle(`storage`, gdpr.allowStorage);
  }

  // if snippets are allowed, execute any GDPR-restricted snippets
  if (gdpr.allowSnippets) {
    snippets.forEach((snippetFunc) => snippetFunc());
  }

  // add mp-is-gdpr or mp-is-not-gdpr class to body; can be used to target styles for GDPR-specific UI
  document.body.className += gdpr.isGdpr ? ` mp-is-gdpr` : ` mp-is-not-gdpr`;
}

/* Initialize GDPR functionality after Mixpanel libs are initialized so that they can be
 * checked for opt in/out status. Mixpanel lib loading time can be inconsistent, so if we
 * fail the first time try again every second until we succeed (up to 5 attempts).
 */
window.gdprInit = () => {
  (function tryInit(attempts=0) {
    try {
      loadIsGdpr().then(initializeGdpr);
    } catch (err) {
      if (attempts >= 5) {
        console.error(`GDPR functionality failed to initialize after ${attempts} attempts: ${err.message}`);
      } else {
        console.warn(`GDPR functionality initialized too early, trying again in 1000ms`);
        setTimeout(() => tryInit(++attempts), 1000);
      }
    }
  })();
};
